import { Injectable, computed, signal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, lastValueFrom } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import { StorageService } from './storage.service';
import { UserRoles } from '../constants/constants';
import { silentNotFound } from '../interceptors/http-context';
import {
  ClientPortalProject, ClientPortalProjectList, ClientPortalProjectListOptions, ClientPortalReviewerProfile, ClientPortalRole,
  ClientPortalSelection, ClientPortalSession, ClientPortalStaffList, ClientPortalStaffListOptions, ClientPortalWorkedTogetherReviewer
} from '../interfaces/client-portal.interface';
import { Response as RoleResponse } from '../../modules/client/dashboard/project/details/response-tab/response.interface';

const SESSION_STORAGE_KEY = 'clientPortalSession';

/**
 * Client-portal API (MP-5750 over the MP-5748 backend namespace).
 *
 * The backend derives everything from the session user: no firm, group or
 * tenant id travels in any request, and the token interceptor sends an empty
 * X-Tenant-Id for this role, which the backend accepts. `/client-portal` is
 * the ONLY resource a CLIENT token may reach; `configurations`, `my`,
 * `documents`, `firms`, `projects`, `users` all answer 403, and the auth
 * interceptor turns a 403 into a forced logout. So the dashboard shell must
 * never fire those for a CLIENT, and this service is the only data source.
 *
 * The session block is fetched once at login and kept in localStorage so the
 * header and sidenav can read it synchronously after a page reload. Logout
 * clears localStorage, which clears it too.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientPortalService {

  /** Session block for the signed-in CLIENT, or null for every other role. */
  readonly session = signal<ClientPortalSession | null>(null);
  /** The firm behind the shared projects; the staff table keys reviewer rating and ranking on it. */
  readonly firmId = computed(() => this.session()?.firm?._id || '');
  readonly firmName = computed(() => this.session()?.firm?.name || '');
  readonly clientGroupName = computed(() => this.session()?.clientGroup?.name || '');
  readonly userName = computed(() => {
    const user = this.session()?.user;
    return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  });

  constructor(private http: HttpBaseService, private storageService: StorageService) {
    this.session.set(this.storageService.getLocalStorageItem(SESSION_STORAGE_KEY) || null);
  }

  /** True when the role cookie says the signed-in user is a client-portal user. */
  isClientPortalUser(): boolean {
    return this.storageService.getRole() === UserRoles.client;
  }

  getSession(): Observable<ClientPortalSession> {
    return this.http.get('client-portal/session');
  }

  /** Fetches the session block and caches it for the shell. Call once right after a CLIENT login. */
  async loadSession(): Promise<ClientPortalSession> {
    const session = await lastValueFrom(this.getSession());
    this.session.set(session);
    this.storageService.setLocalStorageItem(SESSION_STORAGE_KEY, session);
    return session;
  }

  clearSession(): void {
    this.session.set(null);
    this.storageService.setLocalStorageItem(SESSION_STORAGE_KEY, null);
  }

  /** Projects shared with the user's group; STARTED, PUBLISHED and ARCHIVED only, `{projects, count}` like the firm list. */
  getProjects(options: ClientPortalProjectListOptions = {}): Observable<ClientPortalProjectList> {
    let params = new HttpParams();
    (Object.keys(options) as Array<keyof ClientPortalProjectListOptions>).forEach((key) => {
      const value = options[key];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get('client-portal/projects', params.keys().length ? { params } : undefined);
  }

  /** One shared project; the backend answers 404 (never 403) for anything outside the user's group. */
  getProject(projectId: string): Observable<ClientPortalProject> {
    return this.http.get(`client-portal/projects/${projectId}`);
  }

  // ── MP-5752: project details in client mode (MP-5749 scoped endpoints) ────

  /** Staff list of one shared project; same envelope and `searchText` query as the firm endpoint, reviewer allowlisted. */
  getStaffList(projectId: string, options: ClientPortalStaffListOptions = {}): Observable<ClientPortalStaffList> {
    const params = options.searchText ? new HttpParams().set('searchText', options.searchText) : null;
    return this.http.get(`client-portal/projects/${projectId}/staffLists`, params ? { params } : undefined);
  }

  /** Response tab rows, same shape as the firm response tab (reviewer names and offer states). */
  getResponses(projectId: string): Observable<RoleResponse[]> {
    return this.http.get(`client-portal/projects/${projectId}/responses`);
  }

  /** Roles with the sections the Details tab renders; no documents. */
  getRoles(projectId: string): Observable<ClientPortalRole[]> {
    return this.http.get(`client-portal/projects/${projectId}/roles`);
  }

  // ── MP-5754: talent surface (MP-5751 endpoints) ───────────────────────────

  /**
   * Every reviewer with a position, past or current, on the group's projects; a plain
   * array sorted by name, not paged. A 404 (inactive group, capability off) is rendered
   * as the empty state by the screen, not as a global error toast.
   */
  getWorkedTogether(): Observable<ClientPortalWorkedTogetherReviewer[]> {
    return this.http.get('client-portal/workedTogether', silentNotFound());
  }

  /** Redacted profile of one worked-together reviewer; 404 (never 403) for anyone else, rendered by the card. */
  getReviewer(reviewerId: string): Observable<ClientPortalReviewerProfile> {
    return this.http.get(`client-portal/reviewers/${reviewerId}`, silentNotFound());
  }

  // ── MP-5755: soft approve (MP-5753 endpoints) ─────────────────────────────

  /**
   * The one client write: records the group's selection of the reviewer staffed on a
   * position. Idempotent per group (a repeat returns the existing record). The backend
   * answers 400 unless the position is currently OCCUPIED and 404 for anything outside
   * the group's projects; nothing about the position itself changes.
   */
  selectPosition(projectId: string, positionId: string): Observable<ClientPortalSelection> {
    return this.http.post(`client-portal/projects/${projectId}/positions/${positionId}/select`, {});
  }

  /** Soft-withdraws the group's active selection on a position (204 whether or not one existed); allowed on an ended position too. */
  withdrawSelection(projectId: string, positionId: string): Observable<void> {
    return this.http.delete(`client-portal/projects/${projectId}/positions/${positionId}/select`);
  }
}
